import Peers from "@/modules/Peers";
import { commEmitter, dataSyncEmitter, rtcEmitter } from "@/modules/events/emitter";
import { messages } from "@/modules/communication";
import RtcManager from "@/modules/RtcManager";
import { viewerStore } from "viewer/store/viewer";
import { getMediaStream } from "@/modules/stream/getMediaStream";
import { videoConstraints } from "@/modules/stream/constraints";
import "@/modules/RtcManager/rtcWarmUp";

const { setLocalStream, destroyLocalTrack, setRemoteStream } = viewerStore();

const RECONNECT_TIMEOUT = 10 * 1000;

export default class ViewerRtcManager {
  rtc: RtcManager;

  constructor() {
    this.rtc = new RtcManager({
      isPolite: false,
      getStream: () => viewerStore().localStream,
      onPeerConnectionClose: this.reportConnectionClosed
    });
  }

  startListening = () => {
    commEmitter.on(messages.WebRTCCandidate.name, this.rtc.handleWebRTCCandidates);
    commEmitter.on(messages.WebRTCCreateAnswer.name, this.rtc.handleAnswer);
    commEmitter.on(messages.WebRTCCreateOffer.name, this.rtc.handleOffer);
    commEmitter.on(messages.MotionInfoPacket.name, this.handleMotion);
    rtcEmitter.on("connection-state-change", this.handleConnectionStateChange);
    rtcEmitter.on("media-track", this.handleMediaTrack);
  };

  initializeConnection = async (jid: string) => {
    this.rtc.initializePeer(jid);
  };

  createOfferForInstantVideo = async (jid: string): Promise<string> => {
    log.rtc("createOfferForInstantVideo for", jid);
    try {
      const peer = this.rtc.initializePeer(jid);
      this.rtc.setSuppressNegotiation(jid, true);
      if (!peer.peerConnection) throw new Error("No peer connection");

      this.rtc.stream.addRecvOnlyTransceivers(jid);

      const offer = await peer.peerConnection.createOffer();
      await peer.peerConnection.setLocalDescription(offer);

      log.rtc("instant video offer created for", jid);
      return peer.peerConnection.localDescription!.sdp;
    } catch (err) {
      this.rtc.setSuppressNegotiation(jid, false);
      throw err;
    }
  };

  applyInstantVideoAnswer = async (jid: string, answerSdp: string) => {
    log.rtc("applyInstantVideoAnswer for", jid);
    const peer = Peers.get(jid);
    if (!peer) throw new Error("No peer for instant video answer");

    await this.rtc.applyAnswer({ sdp: answerSdp, peer });
    this.rtc.setSuppressNegotiation(jid, false);
  };

  private handleConnectionStateChange = (jid: string) => {
    const peer = Peers.get(jid);
    if (peer == null || peer.peerConnection == null) return;
    if (peer.peerConnection.connectionState === "disconnected") {
      log.rtc("connectionState is disconnected, closing...", jid);
      peer.peerConnection.close();
    }
  };

  // used for correct GUI state
  private reportConnectionClosed = async (jid: string) => {
    rtcEmitter.emit("connection-state-change", jid);
  };

  private handleMediaTrack = (e: RTCTrackEvent, jid: string) => {
    log.rtc("handleMediaTrack fired ", e);
    let remoteStream = e.streams[0];

    if (!remoteStream) {
      remoteStream = viewerStore().remoteStreams[jid] ?? new MediaStream();
      remoteStream.addTrack(e.track);
    }

    setRemoteStream(jid, remoteStream);
  };

  private handleMotion = (motionPacket: {}, jid: string) => {
    log.rtc("received motion info packet");
  };

  recreateIfClosed = async (jid: string) => {
    log.rtc("checking connection", jid);
    const peer = Peers.get(jid);
    if (peer == null || peer.peerConnection == null) return false;
    const { connectionState } = peer.peerConnection;
    if (connectionState !== "closed") return false;
    log.rtc("connectionState is 'closed', restarting...", jid);
    try {
      const { peerConnection } = await this.rtc.recreateConnection(jid);
      setTimeout(() => this.closeIfNotConnected(jid, peerConnection), RECONNECT_TIMEOUT);
      return true;
    } catch (err) {
      log.err("failed connection check:", err, jid);
      return false;
    }
  };

  private closeIfNotConnected = (jid: string, peerConnection: Peer["peerConnection"]) => {
    const connectionState = peerConnection?.connectionState;
    if (!peerConnection || connectionState === "connected" || connectionState === "closed") return;
    log.rtc("Connection wasn't re-established, closing it", connectionState, jid);
    peerConnection.close();
    this.reportConnectionClosed(jid);
  };

  waitForConnectionAndDataChannel = (jid: string) => {
    const peer = Peers.get(jid);
    return new Promise<void>((resolve, reject) => {
      if (!peer) {
        reject("No peer in store");
        return;
      }
      const timeout = setTimeout(() => reject("Connection timeout"), 7000);
      let isConnectionReady = false;
      let isDataChannelReady = false;

      const resolveConnection = () => {
        if (peer.peerConnection?.connectionState === "connected") {
          isConnectionReady = true;
          tryToResolve();
        }
      };
      const resolveDataChannel = () => {
        isDataChannelReady = true;
        tryToResolve();
      };

      const tryToResolve = () => {
        if (isConnectionReady && isDataChannelReady) {
          log.monitoring("Resolved Connection and DataChannel, continuing...");
          clearTimeout(timeout);
          resolve();
        }
      };

      if (peer.peerConnection?.connectionState !== "connected") {
        rtcEmitter.on("connection-state-change", resolveConnection);
        log.monitoring("Waiting for ConnectionState...");
      } else isConnectionReady = true;
      if (peer.commDataChannel?.readyState !== "open") {
        log.monitoring("Waiting for DataChannel...");
        rtcEmitter.on("data-channel-opened", resolveDataChannel);
      } else isDataChannelReady = true;
      tryToResolve();
    });
  };

  startAudioStream = async (jid: string) => {
    const peer = Peers.get(jid);
    const stream = await getMediaStream({
      audio: true,
      video: false
    });
    const audioTrack = stream.getAudioTracks()[0];
    let currentStream = viewerStore().localStream;
    if (currentStream) {
      currentStream.addTrack(audioTrack);
      setLocalStream(currentStream);
    } else setLocalStream(stream);
    if (peer.audioTransceiver) {
      await peer.audioTransceiver.sender.replaceTrack(stream.getAudioTracks()[0]);
    }
    await this.rtc.stream.startSendingStreamTo(jid, { audioOnly: true });
  };

  startVideoStream = async (jid: string) => {
    const peer = Peers.get(jid);
    const stream = await getMediaStream({
      audio: false,
      video: videoConstraints.premium.HD.video
    });
    const videoTrack = stream.getVideoTracks()[0];
    let currentStream = viewerStore().localStream;
    if (currentStream) {
      currentStream.addTrack(videoTrack);
      setLocalStream(currentStream);
    } else setLocalStream(stream);
    if (peer.videoTransceiver) {
      await peer.videoTransceiver.sender.replaceTrack(videoTrack);
    }
    await this.rtc.stream.startSendingStreamTo(jid, { videoOnly: true });
    dataSyncEmitter.emit("local-video-added");
  };

  stopVideoStream = async (jid: string) => {
    await this.rtc.stream.stopSendingStreamTo(jid, {
      stopVideoOnly: true
    });
    destroyLocalTrack("video");
    dataSyncEmitter.emit("local-video-removed");
  };

  stopAudioStream = async (jid: string) => {
    await this.rtc.stream.stopSendingStreamTo(jid, {
      stopAudioOnly: true
    });
    destroyLocalTrack("audio");
  };

  destroy = () => {
    commEmitter.off(messages.WebRTCCandidate.name, this.rtc.handleWebRTCCandidates);
    commEmitter.off(messages.WebRTCCreateAnswer.name, this.rtc.handleAnswer);
    commEmitter.off(messages.WebRTCCreateOffer.name, this.rtc.handleOffer);
    commEmitter.off(messages.MotionInfoPacket.name, this.handleMotion);
    rtcEmitter.off("connection-state-change", this.handleConnectionStateChange);
    rtcEmitter.off("media-track", this.handleMediaTrack);
    this.rtc.destroy();
  };
}
